import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Download } from "lucide-react";
import InlineDocViewer from "@/components/common/InlineDocViewer";

interface DocumentRow {
  id: string;
  document_type: string;
  file_name: string;
  file_path: string;
}

interface DocumentTableProps {
  documents: DocumentRow[];
  emptyMessage?: string;
}

const downloadDocument = async (filePath: string, fileName: string) => {
  const { data } = await supabase.storage
    .from("application-documents")
    .createSignedUrl(filePath, 3600, { download: true });
  if (data?.signedUrl) {
    const a = document.createElement("a");
    a.href = data.signedUrl;
    a.download = fileName;
    a.click();
  }
};

const DocumentTable = ({ documents, emptyMessage = "No documents uploaded yet" }: DocumentTableProps) => {
  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center py-6 text-center border rounded-md">
        <FileText className="h-8 w-8 text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Type</TableHead>
          <TableHead>File Name</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((doc) => (
          <TableRow key={doc.id}>
            <TableCell className="font-medium">{doc.document_type}</TableCell>
            <TableCell className="text-muted-foreground text-sm">{doc.file_name}</TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-1">
                <InlineDocViewer filePath={doc.file_path} fileName={doc.file_name} />
                <Button size="sm" variant="ghost" onClick={() => downloadDocument(doc.file_path, doc.file_name)}>
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default DocumentTable;
